import { z } from 'zod'
import { schemas } from '@repo/contracts'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'

// -- Agent type enum (mirrors sv_features.go Feature*Name constants) --
// Kept for call-site ergonomics; the wire values are enforced by the generated
// ProjectAgentThread schema below.

export const AgentType = {
  SearchAgent: 'SearchAgent',
  EmailAgent: 'EmailAgent',
  ScriptAgent: 'ScriptAgent',
  StoryBoardAgent: 'StoryBoardAgent',
  MarketingAgent: 'MarketingAgent'
} as const

export type AgentType = (typeof AgentType)[keyof typeof AgentType]

// -- Message author enum (mirrors llm.LlmChatRole.String() in types.go) --

export const MessageAuthor = {
  System: 'system',
  User: 'user',
  Assistant: 'assistant',
  UserMention: 'userMention'
} as const

export type MessageAuthor = (typeof MessageAuthor)[keyof typeof MessageAuthor]

// -- Generated response schemas (Go-first; see contracts/). Do not hand-edit;
//    run `task contracts` after changing the Go structs. --

const ProjectAgentThreadSchema = schemas.apihttp_ProjectAgentThread.describe('ProjectAgentThread')
const GetProjectAgentThreadsResponseSchema =
  schemas.apihttp_GetProjectAgentThreadsResponse.describe('GetProjectAgentThreadsResponse')
const ThreadMessageSchema = schemas.apihttp_ThreadMessage.describe('ThreadMessage')
const GetThreadResponseSchema = schemas.apihttp_GetThreadResponse.describe('GetThreadResponse')

export type ProjectAgentThread = z.infer<typeof ProjectAgentThreadSchema>
export type GetProjectAgentThreadsResponse = z.infer<typeof GetProjectAgentThreadsResponseSchema>
export type ThreadMessage = z.infer<typeof ThreadMessageSchema>
export type GetThreadResponse = z.infer<typeof GetThreadResponseSchema>

// ProjectAgentNewThread returns the same shape as a thread list entry.
export type ProjectAgentNewThreadResponse = ProjectAgentThread

// -- SSE response schema (streaming; carved out of the generated contract and
//    kept hand-written on both sides) --

export const AgentSseMessageType = {
  Content: 'content',
  Status: 'status',
  Error: 'error'
} as const

export const AgentSseResponseSchema = z.object({
  threadId: z.number(),
  response: z.string(),
  sseType: z.enum(AgentSseMessageType)
})

export type AgentSseResponse = z.infer<typeof AgentSseResponseSchema>

// -- Edit thread request schema --
// Form-level UX validation (min/max length). The wire contract only requires a
// string title; the stricter bounds are a frontend concern.

export const EditThreadRequestSchema = z.object({
  threadTitle: z.string().min(1, 'Title is required').max(200, 'Title must be 200 characters or less')
})

export type EditThreadRequest = z.infer<typeof EditThreadRequestSchema>

// -- Generic fetch functions --

export async function fetchThreads(projectId: number, agent: AgentType): Promise<GetProjectAgentThreadsResponse> {
  const res = await fetch(`/api/projects/${projectId}/threads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ agent })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, GetProjectAgentThreadsResponseSchema)
}

export async function createThread(
  projectId: number,
  title: string,
  agent: AgentType
): Promise<ProjectAgentNewThreadResponse> {
  const res = await fetch(`/api/projects/${projectId}/threads/new`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ agent, threadTitle: title })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, ProjectAgentThreadSchema)
}

export async function fetchThread(projectId: number, threadId: number): Promise<GetThreadResponse> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, GetThreadResponseSchema)
}

export async function editThread(projectId: number, threadId: number, threadTitle: string): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ threadTitle })
  })
  await apiErrorHandler(res)
}

export async function deleteThread(projectId: number, threadId: number): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function createThreadMessage(
  projectId: number,
  threadId: number,
  content: string,
  includeOrgContent: boolean,
  tagIds: Array<number> = []
): Promise<ThreadMessage> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ content, includeOrgContent, tagIds })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, ThreadMessageSchema)
}

export async function deleteThreadMessage(projectId: number, threadId: number, messageId: number): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}/messages/${messageId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}
